import React from 'react';
import {PriceEventTrackingProvider} from '@udemy-v2/react-currency-formatting';

interface WithPriceEventTrackingContextOptions {
    context: string;
}

/**
 * Higher-order component that wraps a component with PriceEventTrackingProvider.
 * This ensures all pricing events triggered within the wrapped component have the specified context.
 *
 * @param Component - The component to wrap
 * @param options - Configuration options including the context value
 * @returns A wrapped component with price event tracking context
 *
 * @example
 * ```tsx
 * const ComponentWithTracking = withPriceEventTrackingContext(MyComponent, {
 *   context: 'cartSuccessModal'
 * });
 * ```
 */
export function withPriceEventTrackingContext<TProps extends object = Record<string, unknown>>(
    Component: React.ComponentType<TProps>,
    options: WithPriceEventTrackingContextOptions,
): React.ComponentType<TProps> {
    const WrappedComponent = (props: TProps) => {
        return (
            <PriceEventTrackingProvider priceEventTrackingContext={{context: options.context}}>
                <Component {...props} />
            </PriceEventTrackingProvider>
        );
    };

    const componentName = Component.displayName || Component.name || 'Component';
    WrappedComponent.displayName = `withPriceEventTrackingContext(${componentName})`;

    return WrappedComponent;
}
